import {
    RECENTLY_PLAYED_LECTURES_KEY,
    useFetchSubCategory,
} from '../../../lib/fetches';
import { Button } from '../../../components/buttons/Button';
import { PlayLectureCard } from '../../../components/cards/PlayLectureCard';
import API, { API_URL } from '../../../lib/api';
import { useAuth } from '../../../lib/auth';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import AuthLayout from '../../layouts/AuthLayout';
import Skeleton from '../../../components/Skeleton';
import clsx from 'clsx';
import { EmptyState } from '../../../components/emptyStates/EmptyState.tsx';
import { useSWRConfig } from 'swr';
import { UilExternalLinkAlt, UilLock } from '@iconscout/react-unicons';
import { cn } from '../../../lib/helpers.ts';

export default function LecturePage() {
    const { userHasFullLicense, isLoading: userIsLoading } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'LecturePage',
    });

    const { mutate: mutateGlobal } = useSWRConfig();

    const { subCategory: category } = useParams();
    const {
        data: subCategory,
        isLoading,
        mutate,
        error: subCategoryError,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    } = useFetchSubCategory(category!);

    const navigate = useNavigate();
    if (subCategoryError) navigate('/');

    const { hash } = useLocation();

    const [activeLessonIdx, setActiveLessonIdx] = useState<number>(0);

    useEffect(() => {
        // if (!hash) return;

        // Find first not-locked lesson
        let lessonIdx =
            subCategory?.lectures?.findIndex((lesson) => !lesson.is_locked) ??
            -2;

        if (hash && hash.length > 1) {
            const hashLessonIdx =
                subCategory?.lectures?.findIndex(
                    (lesson) =>
                        lesson.slug === hash.slice(1) && !lesson.is_locked
                ) ?? -1;

            if (hashLessonIdx !== -1) lessonIdx = hashLessonIdx;
        }

        setActiveLessonIdx(lessonIdx);
    }, [isLoading, hash]);

    // TODO: set active first not-completed lesson?

    return (
        <AuthLayout>
            <main
                className={clsx(
                    'relative',
                    'mx-auto box-content max-w-2xl py-9',
                    'px-4 sm:px-8 md:px-10'
                )}
            >
                {/*<GradientDecoration color={subCategory?.color} />*/}
                <div className="relative">
                    <div className="mb-16">
                        <h1>
                            {subCategory?.name}

                            {isLoading && (
                                <Skeleton className="h-12 w-1/2 rounded-xl" />
                            )}
                        </h1>
                        <p className="mb-6 text-ink-800">
                            {subCategory?.description}

                            {isLoading && (
                                <Skeleton
                                    count={4}
                                    className="block h-4 w-full rounded-xl"
                                />
                            )}
                        </p>
                        {!isLoading ? (
                            <div>
                                <Link
                                    to={`${API_URL}/methodology`}
                                    target="_blank"
                                    className="inline-block w-auto"
                                >
                                    <Button
                                        color={subCategory?.color ?? 'product'}
                                        variant="secondary"
                                        disabled={
                                            userIsLoading || !userHasFullLicense
                                        }
                                        className={cn(
                                            !userHasFullLicense &&
                                                'relative after:absolute after:inset-0 after:z-10 after:h-full after:w-full after:rounded-2xl after:bg-black/50'
                                        )}
                                    >
                                        <UilExternalLinkAlt
                                            className="mr-2"
                                            size={20}
                                        />
                                        Metodika
                                        {!userHasFullLicense && (
                                            <UilLock className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 text-white" />
                                        )}
                                    </Button>
                                </Link>
                            </div>
                        ) : (
                            <Skeleton className="h-12 w-24 rounded-xl" />
                        )}
                    </div>

                    {(subCategory?.lectures?.length ?? 0) > 0 && (
                        <div className="grid gap-6">
                            {isLoading && (
                                <Skeleton
                                    count={5}
                                    className="h-16 w-full rounded-xl"
                                />
                            )}

                            {subCategory?.lectures?.map((lecture, idx, arr) => (
                                <PlayLectureCard
                                    key={lecture.slug}
                                    lecture={lecture}
                                    color={subCategory?.color}
                                    //
                                    isActive={idx === activeLessonIdx}
                                    isLast={idx === arr.length - 1}
                                    isNextCompleted={
                                        idx < arr.length - 1 &&
                                        (activeLessonIdx === idx + 1 ||
                                            arr[idx + 1].user_completed)
                                    }
                                    //
                                    onLessonStart={() => {
                                        setActiveLessonIdx(idx);
                                    }}
                                    onLessonVideoStart={() => {
                                        API.post(
                                            `/lectures/${lecture.slug}/started`
                                        ).then(() => {
                                            mutateGlobal(
                                                RECENTLY_PLAYED_LECTURES_KEY
                                            );
                                        });
                                    }}
                                    onLessonEnd={() => {
                                        const optimisticData = {
                                            ...subCategory,
                                            lectures:
                                                subCategory?.lectures?.map(
                                                    (item) =>
                                                        item.slug !==
                                                        lecture.slug
                                                            ? item
                                                            : {
                                                                  ...item,
                                                                  user_completed:
                                                                      true,
                                                              }
                                                ),
                                        };

                                        mutate(
                                            API.post(
                                                `/lectures/${lecture.slug}/completed`
                                            ),
                                            {
                                                optimisticData: optimisticData,
                                                populateCache: false,
                                            }
                                        );

                                        // if (idx >= arr.length - 1) return;

                                        // Find next not-locked lesson
                                        const nextLessonIdx = arr.findIndex(
                                            (lesson, i) =>
                                                i > idx && !lesson.is_locked
                                        );

                                        setActiveLessonIdx(nextLessonIdx);
                                    }}
                                />
                            ))}
                        </div>
                    )}
                    {subCategory?.lectures?.length === 0 && (
                        <EmptyState>
                            Toto cvičení zatím neobsahuje žádnou lekci.
                        </EmptyState>
                    )}
                </div>
            </main>
        </AuthLayout>
    );
}
